import { CdkOverlayOrigin, ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Component, input, TemplateRef, viewChild, ViewContainerRef, inject } from '@angular/core';
import { fadeInOnEnterAnimation, slideInUpOnEnterAnimation } from '../animations/index';
import { ButtonComponent } from '../forms/button/button.component';


@Component({
    selector: 'app-dropdown',
    templateUrl: './dropdown.component.html',
    styleUrls: ['./dropdown.component.scss'],
    standalone: true,
    animations: [
        slideInUpOnEnterAnimation({ duration: 150, translate: '10px' }),
        fadeInOnEnterAnimation({ duration: 150 })
    ],
    imports: [CdkOverlayOrigin, ButtonComponent]
})
export class DropdownComponent {
  private overlay = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);

  triggerOrigin = viewChild.required(CdkOverlayOrigin, { read: CdkOverlayOrigin });
  dropdownTemplate = viewChild.required<TemplateRef<unknown>>('dropdownTemplate');
  overlayRef!: OverlayRef;
  isOpen = false;

  buttonTitle = input<string>('Dropdown');
  dots = input<'yes' | 'no' | undefined>(undefined);
  variant = input<'primary' | 'secondary' | 'neutral' | 'ghost' | 'link'>('neutral');
  size = input<'xs' | 'sm' | 'md' | 'lg'>('sm');

  positions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetX: 0,
      offsetY: 4
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
      offsetX: 0,
      offsetY: -4
    },
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetX: 0,
      offsetY: 4
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetX: 0,
      offsetY: -4
    }
  ];

  private closeDropdownBind = this.closeDropdown.bind(this);

  toggleDropdown(): void {
    this.isOpen ? this.closeDropdown() : this.openDropdown();
  }

  openDropdown(): void {
    if (!this.overlayRef) {
      const positionStrategy = this.overlay.position()
        .flexibleConnectedTo(this.triggerOrigin().elementRef)
        .withPositions(this.positions)
        .withFlexibleDimensions(false)
        .withPush(true)
        .withViewportMargin(10);

      this.overlayRef = this.overlay.create({
        positionStrategy,
        hasBackdrop: true,
        backdropClass: 'cdk-overlay-transparent-backdrop'
      });

      this.overlayRef.backdropClick().subscribe(() => this.closeDropdown());
      this.overlayRef.overlayElement.addEventListener('click', this.closeDropdownBind);
      this.overlayRef.detachments().subscribe(() => this.isOpen = false);
    }

    const templatePortal = new TemplatePortal(this.dropdownTemplate(), this.viewContainerRef);
    this.overlayRef.attach(templatePortal);
    this.isOpen = true;
  }

  closeDropdown(): void {
    if (this.overlayRef) {
      this.overlayRef.detach();
    }
    this.isOpen = false;
  }
}
